import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  faBookmark,
  faTrash,
  faTicket,
  faLoveseat,
  faXmark,
  faCalendarAlt,
  faChevronDown,
  faCartXmark,
  faArrowRightFromBracket,
} from '@fortawesome/pro-light-svg-icons';
import { AppSettings } from 'src/app/app-settings';
import { ReservasService } from 'src/app/services/reservas.service';
import { DialogGenericComponent } from '../dialog-generic/dialog-generic.component';
import { sortGroupsByNearestSession } from 'src/app/helpers/reservation-groups';

@Component({
  selector: 'app-reservation-resume',
  templateUrl: './reservation-resume.component.html',
  styleUrls: ['./reservation-resume.component.scss'],
})
export class ReservationResumeComponent implements OnInit, OnChanges, OnDestroy {
    get totalQuantity(): number {
      return this.lines.reduce((acc, line) => acc + (line.quantity || 1), 0);
    }
  @Input() reservationId!: number;

  reservation: any = null;
  loading: boolean = false;
  deletingLineId: number | null = null;
  clearingAll = false;

  faBookmark = faBookmark;
  faTrash = faTrash;
  faTicket = faTicket;
  faSeat = faLoveseat;
  faCalendar = faCalendarAlt;
  faXmark = faXmark;
  faChevron = faChevronDown;
  faCartXmark = faCartXmark;
  faExit = faArrowRightFromBracket;
  urlImages = AppSettings.API_EVENTS_ASSETS;

  private _groupState = new Map<string, boolean>();

  private refreshSub!: Subscription;

  constructor(
    private reservasService: ReservasService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.reservationId) {
      this.loadReservation();
    }
    // Recargar siempre la reserva completa para tener las relaciones (evento, ticket, asiento) populadas
    this.refreshSub = this.reservasService.linesRefresh$.subscribe(() => {
      this.loadReservation();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reservationId'] && !changes['reservationId'].firstChange) {
      this.loadReservation();
    }
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.reservasService.setReservationQuantity(0);
  }

  loadReservation(): void {
    if (!this.reservationId) return;
    this.loading = true;
    this.reservasService.getReservation(this.reservationId).subscribe({
      next: (res) => {
        this.reservation = res?.data || res;
        this.loading = false;
        this.reservasService.setReservationQuantity(this.totalQuantity);
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  get lines(): any[] {
    // La API devuelve las líneas en reservation.cart.cart_lines
    return this.reservation?.cart?.cart_lines || [];
  }

  get total(): number {
    return this.lines.reduce((acc, line) => acc + (line.price_with_discount ?? (+line.price - +line.fee)), 0);
  }

  get groupedLines(): { event: any; session: any; groupKey: string; lines: any[]; total: number; quantityTotal: number; expanded: boolean }[] {
    const map = new Map<string, { event: any; session: any; groupKey: string; lines: any[]; total: number; quantityTotal: number; expanded: boolean }>();
    for (const line of this.lines) {
      const key = `${line.event?.id}_${line.session?.id}`;
      if (!map.has(key)) {
        const existing = this._groupState.get(key);
        map.set(key, { event: line.event, session: line.session, groupKey: key, lines: [], total: 0, quantityTotal: 0, expanded: existing !== undefined ? existing : true });
      }
      const group = map.get(key)!;
      group.lines.push(line);
      group.quantityTotal += (line.quantity || 1);
      group.total += line.price_with_discount ?? (+line.price - +line.fee);
    }
    return sortGroupsByNearestSession(Array.from(map.values()));
  }

  toggleGroup(groupKey: string): void {
    const current = this._groupState.has(groupKey) ? this._groupState.get(groupKey)! : true;
    this._groupState.set(groupKey, !current);
  }

  formatNumber(floatValue = 1, decimals = 2): string {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      style: 'currency',
      currency: 'EUR',
    }).format(floatValue);
  }

  getLineSeatInfo(line: any): string {
    if (line.bookings?.length > 0) {
      const seat = line.bookings[0].seat;
      if (seat) {
        const parts: string[] = [];
        if (seat.map_section?.name) parts.push(seat.map_section.name);
        if (seat.row) parts.push(`Fila ${seat.row}`);
        if (seat.seat) parts.push(`Butaca ${seat.seat}`);
        return parts.join(' · ');
      }
    }
    return '';
  }

  clearLines(): void {
    this.dialog.open(DialogGenericComponent, {
      data: {
        title: 'Limpiar reserva',
        subtitle: '¿Seguro que quieres eliminar todas las entradas de la reserva?',
        icon: 'warning',
        buttons: [
          { label: 'Cancelar', type: 'secondary', action: () => this.dialog.closeAll() },
          {
            label: 'Sí, limpiar',
            type: 'danger',
            action: () => {
              this.dialog.closeAll();
              this.clearingAll = true;
              const allSeatIds: number[] = (this.lines || []).flatMap((l: any) =>
                (l.bookings || []).map((b: any) => b?.seat?.id).filter((id: any) => !!id)
              );
              this.reservasService.clearLines(this.reservationId).subscribe({
                next: () => {
                  this.clearingAll = false;
                  if (this.reservation?.cart) {
                    this.reservation.cart.cart_lines = [];
                  }
                  this.reservasService.setReservationQuantity(0);
                  this.reservasService.triggerSeatRemoved(allSeatIds);
                },
                error: () => { this.clearingAll = false; }
              });
            }
          }
        ],
      }
    });
  }

  deleteLine(lineId: number): void {
    if (!lineId) return;
    this.deletingLineId = lineId;
    const line = this.reservation?.cart?.cart_lines?.find((l: any) => l.id === lineId);
    const seatIds: number[] = (line?.bookings || []).map((b: any) => b?.seat?.id).filter((id: any) => !!id);
    this.reservasService.deleteLine(this.reservationId, lineId).subscribe({
      next: () => {
        this.deletingLineId = null;
        if (this.reservation?.cart?.cart_lines) {
          this.reservation.cart.cart_lines = this.reservation.cart.cart_lines.filter((l: any) => l.id !== lineId);
        }
        this.reservasService.triggerSeatRemoved(seatIds);
      },
      error: () => {
        this.deletingLineId = null;
        this.dialog.open(DialogGenericComponent, {
          data: {
            title: 'Error',
            subtitle: 'No se ha podido eliminar la línea.',
            icon: 'error',
            buttons: [{ label: 'Cerrar', action: () => this.dialog.closeAll() }],
          },
        });
      },
    });
  }

  exitReservationMode(): void {
    if (!this.reservationId) {
      this.router.navigate(['/home']);
      return;
    }
    this.dialog.open(DialogGenericComponent, {
      data: {
        title: 'Cancelar reserva',
        subtitle: `¿Seguro que quieres cancelar la reserva "${this.reservation?.name || ''}"? Se liberarán todos los asientos.`,
        icon: 'warning',
        buttons: [
          { label: 'No cancelar', type: 'secondary', action: () => this.dialog.closeAll() },
          {
            label: 'Sí, cancelar',
            type: 'danger',
            action: () => {
              this.dialog.closeAll();
              this.reservasService.deleteReservation(this.reservationId).subscribe({
                next: () => this.router.navigate(['/reservas']),
                error: () => {
                  this.dialog.open(DialogGenericComponent, {
                    data: {
                      title: 'Error',
                      subtitle: 'No se ha podido cancelar la reserva.',
                      icon: 'error',
                      buttons: [{ label: 'Cerrar', action: () => this.dialog.closeAll() }],
                    },
                  });
                },
              });
            }
          }
        ]
      }
    });
  }

  goToReservation(): void {
    this.router.navigate(['/reservas', this.reservationId]);
  }

  exitToSaleHome(): void {
    const dialogRef = this.dialog.open(DialogGenericComponent, {
      data: {
        title: 'Salir del modo reserva',
        subtitle: '¿Seguro que quieres salir? La reserva se mantendrá guardada y podrás continuarla más tarde.',
        icon: 'warning',
        buttons: [
          { label: 'Cancelar', type: 'secondary', action: () => dialogRef.close() },
          {
            label: 'Sí, salir',
            type: 'primary',
            action: () => {
              dialogRef.close();
              this.reservasService.setActiveReservation(null);
              this.router.navigateByUrl('/home');
            }
          }
        ]
      }
    });
  }

  trackByGroup(_index: number, group: any): string {
    return group.groupKey;
  }

  trackByLine(_index: number, line: any): number {
    return line.id;
  }
}
